import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useProductName, useBrand } from "@/context/BrandContext";
import { useLiveCallsCount } from "@/hooks/useLiveCallsCount";
import { usePendingPaymentsCount } from "@/hooks/usePendingPaymentsCount";
import { canViewPath } from "@/lib/permissions";
import api from "@/lib/api";
import {
  SquaresFour, UsersThree, Storefront, Megaphone, ShareNetwork, WaveSine,
  Tag, ListDashes, Gear, Wallet, Phone, PhoneCall, CaretLeft, CaretDown,
  Brain, Broadcast, ListMagnifyingGlass, Receipt, CreditCard, Gauge,
  AddressBook, HardDrives, ChartLineUp, SlidersHorizontal, Robot, SpeakerHigh, Microphone, Books,
  MusicNotes, Package, ShieldCheck,
  PaintBrush, UserPlus, CurrencyDollar, ListBullets, IdentificationCard,
  Path, Truck, PhoneIncoming, UsersFour, Clock, ChartPieSlice, ChartBar,
  Plugs, ChatText, EnvelopeSimple, Ticket, CalendarBlank,
} from "@phosphor-icons/react";

// Menus support two node types: a leaf { to, label, icon } and a collapsible
// group { group, icon, children:[leaf...] }.
const NAV = {
  admin: [
    { to: "/dashboard", label: "Dashboard", icon: SquaresFour, end: true },
    { group: "Accounts", icon: AddressBook, children: [
      { to: "/customers", label: "Customers", icon: UsersThree },
      { to: "/resellers", label: "Resellers", icon: Storefront },
      { to: "/users", label: "Users", icon: UserPlus, adminOnly: true },
    ] },
    { to: "/templates", label: "Global Campaigns", icon: Megaphone },
    { to: "/live-calls", label: "Live Calls", icon: Broadcast },
    { group: "Servers", icon: HardDrives, children: [
      { to: "/servers/opensips", label: "OpenSIPS Servers", icon: ShareNetwork },
      { to: "/servers/media", label: "Media Servers", icon: WaveSine },
      { to: "/servers/ai-engines", label: "AI Engine Servers", icon: Brain },
      { to: "/servers/pd", label: "PD Servers", icon: HardDrives },
      { to: "/servers/webrtc", label: "WebRTC Servers", icon: Broadcast },
      { to: "/servers/vicidial", label: "VICIdial Servers", icon: PhoneCall },
      { to: "/servers/pricing", label: "Server Pricing", icon: Tag },
      { to: "/installation", label: "Installation", icon: Package },
    ] },
    { group: "VPN", icon: ShieldCheck, children: [
      { to: "/vpn/wireguard", label: "WireGuard", icon: ShareNetwork },
      { to: "/vpn/ssh-tunnel", label: "SSH tunnel", icon: Plugs },
    ] },
    { group: "AI Bots", icon: Robot, children: [
      { to: "/ai-bots/models", label: "AI Models", icon: Robot },
      { to: "/ai-bots/tts", label: "TTS", icon: SpeakerHigh },
      { to: "/ai-bots/stt", label: "STT", icon: Microphone },
    ] },
    { group: "3rd Party Providers", icon: Plugs, children: [
      { to: "/providers/sms", label: "SMS Providers", icon: ChatText },
      { to: "/providers/email", label: "Email Providers", icon: EnvelopeSimple },
    ] },
    { group: "Billing", icon: Receipt, children: [
      { to: "/billing/pricing", label: "Billing", icon: Wallet },
      { to: "/billing/predictive-pricing", label: "Predictive Pricing", icon: ChartLineUp },
      { to: "/billing/ai-pricing", label: "AI Pricing", icon: Brain },
      { to: "/billing/payments", label: "Payments", icon: CreditCard },
    ] },
    { to: "/tickets", label: "Tickets", icon: Ticket },
    { group: "Reports", icon: ChartLineUp, children: [
      { to: "/cdr/outbound", label: "Outbound CDR", icon: ListDashes },
      { to: "/cdr/inbound", label: "Inbound CDR", icon: PhoneIncoming },
      { to: "/cdr/reseller", label: "Reseller CDR", icon: Storefront },
      { to: "/reports/outbound-disposition", label: "Outbound Disposition", icon: ChartPieSlice },
      { to: "/reports/inbound-disposition", label: "Inbound Disposition", icon: ChartPieSlice },
      { to: "/reports/campaign-disposition", label: "Campaign Disposition", icon: ChartBar },
      { to: "/reports/timesheet", label: "Timesheet", icon: Clock },
      { to: "/reports/live-stats", label: "Live Stats", icon: ChartLineUp },
      { to: "/reports", label: "Audit logs", icon: ListMagnifyingGlass, end: true },
      { to: "/reports/stats", label: "Stats Report", icon: Gauge },
      { to: "/reports/payment-history", label: "Customer Payments", icon: Receipt, end: true },
      { to: "/reports/reseller-payment", label: "Reseller Payment", icon: Storefront },
      { to: "/reports/commissions", label: "Commissions", icon: CurrencyDollar },
    ] },
    { group: "System", icon: Gear, children: [
      { to: "/staff-knowledge-bases", label: "Knowledge Bases", icon: Books },
      { to: "/audios", label: "Hold Music / Audios", icon: MusicNotes },
      { to: "/callback-reconciliation", label: "Callback reconciliation", icon: CalendarBlank },
      { to: "/settings", label: "System Settings", icon: SlidersHorizontal },
      { to: "/branding", label: "Branding", icon: PaintBrush },
      { to: "/system-logs", label: "System Logs", icon: ListMagnifyingGlass },
    ] },
  ],
  reseller: [
    { to: "/dashboard", label: "Dashboard", icon: SquaresFour, end: true },
    { to: "/customers", label: "Customers", icon: UsersThree },
    { to: "/live-calls", label: "Live Calls", icon: Broadcast },
    { to: "/call-limit", label: "Call Limit", icon: Gauge },
    { group: "Billing", icon: Receipt, children: [
      { to: "/payments", label: "Payments", icon: CreditCard },
      { to: "/billing", label: "Billing", icon: Wallet },
      { to: "/reseller-pricing", label: "Pricing", icon: Tag },
      { to: "/white-labeling", label: "White labeling", icon: PaintBrush },
    ] },
    { group: "Reports", icon: ChartLineUp, children: [
      { to: "/cdr/outbound", label: "Outbound CDR", icon: ListDashes },
      { to: "/cdr/inbound", label: "Inbound CDR", icon: PhoneIncoming },
      { to: "/reports/outbound-disposition", label: "Outbound Disposition", icon: ChartPieSlice },
      { to: "/reports/inbound-disposition", label: "Inbound Disposition", icon: ChartPieSlice },
      { to: "/reports/campaign-disposition", label: "Campaign Disposition", icon: ChartBar },
      { to: "/reports/timesheet", label: "Timesheet", icon: Clock },
      { to: "/reports/live-stats", label: "Live Stats", icon: ChartLineUp },
      { to: "/reports/billing-history", label: "Billing History", icon: Receipt },
      { to: "/reports/payment-history", label: "Customer payments", icon: CreditCard },
    ] },
  ],
  customer: [
    { to: "/dashboard", label: "Dashboard", icon: SquaresFour, end: true },
    { to: "/live-calls", label: "Live Calls", icon: Broadcast },
    { group: "Operations", icon: Megaphone, children: [
      { to: "/team", label: "Users & Groups", icon: UsersThree },
      { to: "/campaigns", label: "Campaigns", icon: Megaphone },
      { to: "/lists", label: "Lists", icon: ListBullets, leadsMode: "from_list" },
      { to: "/leads", label: "Leads", icon: IdentificationCard, leadsMode: "from_lead" },
      { to: "/text-to-speech", label: "Text to Speech", icon: SpeakerHigh },
      { to: "/knowledge-bases", label: "Knowledge Bases", icon: Books },
    ] },
    { group: "Routing", icon: Path, children: [
      { to: "/routing/vendors", label: "Vendors Routing", icon: Truck },
      { to: "/servers/vicidial", label: "VICIdial Servers", icon: PhoneCall },
      { to: "/routing/dids", label: "DID Management", icon: PhoneIncoming },
      { to: "/routing/inbound-groups", label: "Inbound Groups", icon: UsersFour },
      { to: "/providers/sms", label: "SMS Providers", icon: ChatText },
      { to: "/providers/email", label: "Email Providers", icon: EnvelopeSimple },
    ] },
    { group: "Report", icon: ChartLineUp, children: [
      { to: "/cdr/outbound", label: "Outbound CDR", icon: ListDashes },
      { to: "/cdr/inbound", label: "Inbound CDR", icon: PhoneIncoming },
      { to: "/reports/outbound-disposition", label: "Outbound Disposition", icon: ChartPieSlice },
      { to: "/reports/inbound-disposition", label: "Inbound Disposition", icon: ChartPieSlice },
      { to: "/reports/campaign-disposition", label: "Campaign Disposition", icon: ChartBar },
      { to: "/reports/timesheet", label: "Timesheet", icon: Clock },
      { to: "/reports/live-stats", label: "Live Stats", icon: ChartLineUp },
    ] },
    { to: "/tickets", label: "Tickets", icon: Ticket },
    { group: "Settings", icon: Gear, children: [
      { to: "/account-dispositions", label: "Disposition", icon: Tag },
      { to: "/account-audios", label: "Audios", icon: MusicNotes, audioLibrary: true },
      { to: "/call-limit", label: "Call Limits", icon: Gauge },
    ] },
  ],
};

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");
const childActive = (children, pathname) =>
  children.some((c) => pathname === c.to || pathname.startsWith(c.to + "/"));

function filterNavForStaff(navItems, user) {
  return navItems
    .map((it) => {
      if (it.group) {
        const children = it.children.filter(
          (c) => !c.adminOnly && canViewPath(user, c.to)
        );
        if (!children.length) return null;
        return { ...it, children };
      }
      if (it.adminOnly) return null;
      return canViewPath(user, it.to) ? it : null;
    })
    .filter(Boolean);
}

export default function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const { brand } = useBrand();
  const productName = useProductName();
  const role = user?.role;
  const [leadsOptions, setLeadsOptions] = useState("from_list");
  const [allowAudioUploads, setAllowAudioUploads] = useState(false);
  useEffect(() => {
    if (role !== "customer") return;
    let cancelled = false;
    api.get("/customer-settings")
      .then(({ data }) => {
        if (cancelled) return;
        setLeadsOptions(data?.leads_options || "from_list");
        setAllowAudioUploads(!!data?.allow_customer_audio_uploads);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [role, user?.tenant_id]);
  const items = useMemo(() => {
    if (role === "staff") return filterNavForStaff(NAV.admin, user);
    const raw = NAV[role] || [];
    if (role !== "customer") return raw;
    // Leads Options: show Lists XOR Leads in nav (same underlying lead records).
    // Audios: only when admin enabled customer audio uploads for this tenant.
    return raw.map((it) => {
      if (!it.group || !it.children) return it;
      const children = it.children.filter((c) => {
        if (c.audioLibrary) return allowAudioUploads;
        if (!c.leadsMode) return true;
        return c.leadsMode === leadsOptions;
      });
      return { ...it, children };
    });
  }, [user, role, leadsOptions, allowAudioUploads]);
  const liveCount = useLiveCallsCount();
  const pendingPayments = usePendingPaymentsCount();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState({});

  // Auto-expand the group that owns the active route.
  useEffect(() => {
    items.forEach((it) => {
      if (it.group && childActive(it.children, location.pathname)) {
        setOpenGroups((p) => (p[it.group] ? p : { ...p, [it.group]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, role, items]);

  const Leaf = ({ it, nested }) => (
    <NavLink
      to={it.to}
      end={it.end}
      data-testid={`nav-${slug(it.label)}`}
      className={({ isActive }) =>
        cn(
          "relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200",
          collapsed && "justify-center px-0",
          nested && !collapsed && "pl-9",
          isActive
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )
      }
    >
      <it.icon className="w-5 h-5 shrink-0" weight="duotone" />
      {!collapsed && <span className="truncate">{it.label}</span>}
      {it.to === "/live-calls" && liveCount > 0 && (
        collapsed ? (
          <span
            data-testid="live-calls-badge-dot"
            className="absolute top-1.5 right-2.5 h-2.5 w-2.5 rounded-full bg-[hsl(var(--success))] ring-2 ring-card"
          />
        ) : (
          <span
            data-testid="live-calls-badge"
            className="ml-auto inline-flex items-center gap-1 rounded-full bg-[hsl(var(--success)/0.15)] px-2 py-0.5 text-xs font-semibold text-[hsl(var(--success))]"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-70" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--success))]" />
            </span>
            {liveCount}
          </span>
        )
      )}
      {(it.to === "/payments" || it.to === "/billing/payments") && pendingPayments > 0 && (
        collapsed ? (
          <span
            data-testid="pending-payments-dot"
            className="absolute top-1.5 right-2.5 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-card"
          />
        ) : (
          <span
            data-testid="pending-payments-badge"
            className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-xs font-bold"
          >
            {pendingPayments}
          </span>
        )
      )}
    </NavLink>
  );

  return (
    <aside
      className={cn(
        "shrink-0 border-r border-border bg-card/60 backdrop-blur-sm flex flex-col transition-[width] duration-300",
        collapsed ? "w-20" : "w-64"
      )}
      style={{ transitionTimingFunction: "cubic-bezier(0.4,0,0.2,1)" }}
      data-testid="sidebar"
    >
      <div className={cn("h-16 flex items-center gap-3 px-5 border-b border-border", collapsed && "justify-center px-0")}>
        {brand.logo_url ? (
          <img
            src={brand.logo_url}
            alt={productName || "Logo"}
            className="w-9 h-9 object-contain shrink-0"
          />
        ) : productName ? (
          <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground grid place-items-center shrink-0">
            <Phone className="w-5 h-5" weight="fill" />
          </div>
        ) : (
          <div className="w-9 h-9 rounded-xl bg-muted animate-pulse shrink-0" aria-hidden />
        )}
        {!collapsed && (
          <div className="leading-tight">
            {productName ? (
              <>
                <p className="font-display font-bold tracking-tight">{productName}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {brand.slogan || brand.tagline || "Control Plane"}
                </p>
              </>
            ) : (
              <div className="space-y-1.5" aria-hidden>
                <div className="h-4 w-16 rounded bg-muted animate-pulse" />
                <div className="h-2.5 w-20 rounded bg-muted/70 animate-pulse" />
              </div>
            )}
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {collapsed
          ? items.flatMap((it) =>
              it.group
                ? it.children.map((c) => <Leaf key={c.to} it={c} />)
                : [<Leaf key={it.to} it={it} />]
            )
          : items.map((it) => {
              if (!it.group) return <Leaf key={it.to} it={it} />;
              const isOpen = !!openGroups[it.group];
              const active = childActive(it.children, location.pathname);
              const showGroupBadge = (role === "admin" || role === "staff")
                && it.children.some((c) => c.to === "/billing/payments")
                && pendingPayments > 0;
              return (
                <div key={it.group}>
                  <button
                    type="button"
                    data-testid={`nav-group-${slug(it.group)}`}
                    onClick={() => setOpenGroups((p) => ({ ...p, [it.group]: !p[it.group] }))}
                    className={cn(
                      "w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200",
                      active && !isOpen
                        ? "text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <it.icon className="w-5 h-5 shrink-0" weight="duotone" />
                    <span className="truncate">{it.group}</span>
                    {showGroupBadge && (
                      <span
                        data-testid="pending-payments-badge-group"
                        className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-xs font-bold"
                      >
                        {pendingPayments}
                      </span>
                    )}
                    <CaretDown className={cn("w-4 h-4 transition-transform duration-200", showGroupBadge ? "ml-1.5" : "ml-auto", isOpen && "rotate-180")} />
                  </button>
                  {isOpen && (
                    <div className="mt-1 space-y-1">
                      {it.children.map((c) => (
                        <Leaf key={c.to} it={c} nested />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
      </nav>

      <button
        onClick={onToggle}
        data-testid="sidebar-collapse-btn"
        className="h-12 border-t border-border flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
      >
        <CaretLeft className={cn("w-4 h-4 transition-transform duration-300", collapsed && "rotate-180")} />
        {!collapsed && <span>Collapse</span>}
      </button>
    </aside>
  );
}
